import { QueryClient } from "@tanstack/react-query";
import { Duration } from "luxon";

import { ApiError } from "@storyboard/web-client";

/**
 * Per-domain freshness windows. Interval-refetching queries (device usage, unactivated members)
 * pass their own `refetchInterval` and use these only to dedupe concurrent mounts.
 */
export const staleTimes = {
  /** Identity changes only via logout — refetch on demand, never in the background. */
  userInfo: Number.POSITIVE_INFINITY,
  /** Roster-adjacent reads a dispatcher expects to be fresh-ish without hammering the API. */
  members: Duration.fromObject({ minutes: 1 }).toMillis(),
  workflows: Duration.fromObject({ seconds: 30 }).toMillis(),
  /** Search results are keyed by request, so a repeated identical search within a minute is served from cache. */
  search: Duration.fromObject({ minutes: 1 }).toMillis(),
} as const;

/** 4xx responses are the caller's bug or state, not weather — never retry them. */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: retryUnlessClientError,
      staleTime: 30_000,
    },
    mutations: {
      retry: false,
    },
  },
});
